import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import "./Login.scss";
import {
  CustomLoadingButtonFilled,
} from "../../components/ui/CustomButton";
import { loginStart, loginSuccess, loginFailure } from "../../redux/userSlice";
import newRequest from "../../utils/newRequest";
import { motion } from "framer-motion";
import { useTranslation } from 'react-i18next';
import i18n from '../../utils/i18n';
import { muiSelectStyles_dark, muiSelectStyles_light } from '../../components/ui/CustomSelect';
import {
  Select,
  MenuItem,
} from "@mui/material";
import { Navigate } from 'react-router-dom';
import { ScaleLoader } from "react-spinners";
import { useSocket } from "../../redux/useSocket";
import OtpInput from 'react-otp-input';
import { IoEye, IoEyeOff } from "react-icons/io5";
import { showToast } from '../../toastService';


// Function to get browser from userAgent string
const getBrowser = (userAgent) => {
  if (userAgent.includes("Firefox")) {
    return "Firefox";
  } else if (userAgent.includes("Chrome")) {
    return "Chrome";
  } else if (userAgent.includes("Safari")) {
    return "Safari";
  } else if (userAgent.includes("MSIE") || userAgent.includes("Trident")) {
    return "Internet Explorer";
  } else {
    return "Unknown";
  }
};

// Function to get browser version from userAgent string
const getBrowserVersion = (userAgent) => {
  const match = userAgent.match(/(Chrome|Firefox|Safari|MSIE|Trident)\/(\d+)/);
  return match ? match[2] : "Unknown";
};

const Login = () => {
  const { currentTheme } = useSelector((state) => state.theme);
  const { currentUser, sessionValidated } = useSelector(
    (state) => state.user
  );
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const socket = useSocket();
  const socketId = useSelector((state) => state.socket.socketId);
  const [currentView, setCurrentView] = useState("login");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [showError, setShowError] = useState(false);
  const [otpView, setOtpView] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isProcessingTitle, setIsProcessingTitle] = useState(t('pleaseWait'));
  const loading = useSelector((state) => state.user.loading);
  const [serverOtp, setServerOtp] = useState("");
  const [userDocid, setUserDocid] = useState("");
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState("");
  // Browser info
  const userAgent = navigator.userAgent;
  const browserInfo = {
    userAgent: userAgent,
    browser: getBrowser(userAgent),
    browserVersion: getBrowserVersion(userAgent),
    platform: navigator.platform,
  };
  const usernameRef = useRef(null);
  const newPasswordRef = useRef(null);
  const [enteredResetPassword, setEnteredResetPassword] = useState(null);
  const [enteredVerifyPassword, setEnteredVerifyPassword] = useState(null);
  const [passwordError, setPasswordError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showVerifyPassword, setShowVerifyPassword] = useState(false);
  const [enableReset, setEnableReset] = useState(false);
  const [showMainPassword, setShowMainPassword] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(localStorage.getItem("language") || "en");


  const handleLanguageChange = (event) => {
    try {
      const newLang = event.target.value;
      // console.log(`newLang .... ${newLang}`);
      setSelectedLanguage(newLang);
      localStorage.setItem("language", newLang);
      i18n.changeLanguage(newLang);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    i18n.changeLanguage(selectedLanguage);
  }, [selectedLanguage]);

  // Effect: once user + socketId ready, register session
  useEffect(() => {
    if (currentUser && socketId) {

      if (socketId) {
        socket.emit("newUser", {
          ...currentUser,
          session_browser: browserInfo?.browser,
          session_browser_version: browserInfo?.browserVersion,
          session_platform: browserInfo?.platform,
        });
      }

      newRequest.post("/session/add", {
        session_socket_id: socketId,
        session_user_id: currentUser?.usr_id,
        session_user_name: currentUser?.usr_full_name,
        session_org_name: currentUser?.usr_org_name,
        session_branch_code: currentUser?.usr_unit_code,
        session_branch_name: currentUser?.bizunit_name_xx,
        session_fm: new Date(),
        session_browser: browserInfo?.browser,
        session_browser_version: browserInfo?.browserVersion,
        session_platform: browserInfo?.platform,
      });
      console.log(`Session registered with socketId ${socketId} for user ${currentUser?.usr_id}`);
    }
    if (usernameRef.current) {
      usernameRef.current?.focus();
    }
  }, [currentUser, socketId]);

  const handleLogin = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    dispatch(loginStart());
    setShowError(false);
    setError("");

    const TIMEOUT_DURATION = 120000;
    let timeoutId;

    try {
      timeoutId = setTimeout(() => {
        dispatch(loginFailure());
        setShowError(true);
        setError("Login request timed out. Please try again.");
      }, TIMEOUT_DURATION);

      console.time("Login");
      const res = await newRequest.post("/auth/login", {
        usr_un: username,
        usr_pw: password,
        browserInfo,
      });
      console.timeEnd("Login");

      clearTimeout(timeoutId);

      // console.log(`/auth/login user ${JSON.stringify(res.data, null, 2)}`);

      if (res.data.result === "0") {
        dispatch(loginSuccess(res.data));

        // Emit newUser once login succeeds
        if (socket && socket.connected) {
          socket.emit("newUser", {
            ...res.data,
            session_browser: browserInfo?.browser,
            session_browser_version: browserInfo?.browserVersion,
            session_platform: browserInfo?.platform,
          });
          console.log(`Emitted newUser for userId ${res.data?.usr_id}, socketId ${socket.id}`);
        } else {
          console.warn("Socket not ready, skipping emit newUser");
        }

        console.log(`socketId ... ${socketId} for userId ${res.data?.usr_id}`);

        await newRequest.post("/session/add", {
          session_socket_id: socketId,
          session_user_id: res.data?.usr_id,
          session_user_name: res.data?.usr_full_name,
          session_org_name: res.data?.usr_org_name,
          session_branch_code: res.data?.usr_unit_code,
          session_branch_name: res.data?.bizunit_name_xx,
          session_fm: new Date(),
          session_browser: browserInfo?.browser,
          session_browser_version: browserInfo?.browserVersion,
          session_platform: browserInfo?.platform,
        });

        navigate("/", { replace: true });
      } else {
        dispatch(loginFailure());
        setShowError(true);
        setError("Invalid Login details. Please try again...");
      }
    } catch (err) {
      console.log(err);
      clearTimeout(timeoutId);
      dispatch(loginFailure());
      setShowError(true);
      setError("Something went wrong. Please try again.");
    }
  };

  const handleRequestOtp = async (e) => {
    if (e?.preventDefault) e.preventDefault();

    if (!username) setError("Please enter username");

    const res = await newRequest.post("/adUser/resetOtp", {
      usr_un: username,
      browserInfo,
    });

    if (res.data?.msg === "user not found") {
      setError("Invalid username");
    } else if (res.data?.msg === "sent") {
      setError("");
      setServerOtp(res.data?.otp);
      setUserDocid(res.data?.user_docid);
      setOtpView(true);
    }
  };

  const handleResetPassword = async (e) => {
    if (e?.preventDefault) e.preventDefault();

    const res = await newRequest.post("/adUser/passwordResetNote", {
      current_password: "",
      new_password: enteredVerifyPassword,
      user_docid: userDocid,
      action: "resetPassword",
      browserInfo,
    });
    // console.log(`passwordResetNote ${JSON.stringify(res?.data, null, 2)}`);
    if (res.data?.msg === "success") {
      showToast(t('passwordChangedLogin'), "success");
      setEnteredResetPassword(null);
      setEnteredVerifyPassword(null);
      setPasswordError("");
      setShowPassword(false);
      setShowVerifyPassword(false);
      setEnableReset(false);
      setCurrentView("login");
    } else {
      showToast(t('passwordChangedError'), "error");
    }
  };

  // Function to handle OTP change
  const handleOtpChange = (newOtp) => {
    setOtp(newOtp);
    // Start validation when OTP reaches 6 digits
    if (newOtp.length === 6) {
      validateOtp(newOtp);
    }
  };

  const validateOtp = async (enteredOtp) => {
    setIsProcessing(true);
    // console.log("Server OTP:", serverOtp);
    // console.log("Validating OTP:", enteredOtp);
    try {
      if (enteredOtp === serverOtp) {
        setIsProcessing(false);
        setOtpError("");
        setCurrentView("verifyPassword");
        setTimeout(() => newPasswordRef.current?.focus(), 100);
      } else {
        setIsProcessing(false);
        setOtpError("Invalid OTP entered. Please try again.");
      }
    } catch (error) {
      console.log(error);
    }
  };

  //  Rendering logic comes last
  if (currentUser && sessionValidated) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="login-page">
      <motion.div
        className="login-wrapper"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, transition: { duration: 1 } }}
        exit={{ opacity: 0, transition: { duration: 1 } }}
      >
        {currentView === "login" && <h1 className='my-logo-text lg'>{t('appName')}</h1>}
        {["resetPassword", "verifyPassword"].includes(currentView) && <h1 className='main-title'>{t('resetPassword')}</h1>}
        {currentView === "login" &&
          <div className="login-content">
            <div className="login-left">
              <motion.div
                className="hero-text"
                initial={{
                  opacity: 0,
                }}
                animate={{
                  opacity: 1,
                }}
                exit={{
                  opacity: 0,
                }}
              >
                {/* <h1>
                {t('poweringHealthcare')} {" "}
                <span> {t('accessEfficiency')} </span>
              </h1> */}
                <h1>
                  {t('appTagLineA')} {" "}
                  <span> {t('appTagLineB')} </span>
                </h1>
              </motion.div>
            </div>
            <div className="login-right">
              <form className="login-right-content">
                {/* <h1 className='main-title'>Craft</h1> */}
                {/* <h1 className="main-title">{t('login')}</h1> */}
                <div className="input-item-with-label">
                  <input
                    ref={usernameRef}
                    id="username"
                    name="username"
                    type="text"
                    placeholder={t('username').toUpperCase()}
                    value={username}
                    className="form-input-item"
                    width={"300px"}
                    onChange={(e) => {
                      try {
                        if (error) {
                          setError("");
                        }
                        setUsername(e.target.value);
                      } catch (error) {
                        console.log(error);
                      }
                    }}
                  />
                </div>
                <div className="input-item-with-label" style={{ position: "relative" }}>
                  <input
                    id="password"
                    name="password"
                    type={showMainPassword ? "text" : "password"}
                    placeholder={t('password').toUpperCase()}
                    value={password}
                    width={"300px"}
                    className="form-input-item"
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleLogin(e); }}
                  />
                  <span
                    onClick={() => setShowMainPassword((prev) => !prev)}
                    style={{
                      position: "absolute",
                      right: "8px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      cursor: "pointer",
                      fontSize: "0.9rem",
                    }}
                  >
                    {showMainPassword ? <IoEyeOff size={20} /> : <IoEye size={20} />}
                  </span>
                </div>
                {showError && <span className="error"> {error} </span>}
                <CustomLoadingButtonFilled
                  type="submit" width={"350px"}
                  disabled={loading}
                  onClick={(e) => handleLogin(e)}>
                  {loading ? t("loggingIn") : t("login")}
                </CustomLoadingButtonFilled>
                {/* Language Selector */}
                <div className="language-selector">
                  <label htmlFor="language">{t("selectLanguage")}: </label>
                  <Select
                    id="language"
                    name="language"
                    value={selectedLanguage}
                    onChange={handleLanguageChange}
                    variant="outlined"
                    size="small"
                    height={"30px"}
                    sx={currentTheme === "dark" ? muiSelectStyles_dark(false, false, 'max-content', "32px").select : muiSelectStyles_light(false, false, 'max-content', "32px").select}
                    MenuProps={{
                      PaperProps: {
                        sx: currentTheme === "dark" ? muiSelectStyles_dark().menu.paper : muiSelectStyles_light().menu.paper
                      }
                    }}
                  >
                    <MenuItem value="en" sx={{ height: '30px' }}>English</MenuItem>
                    <MenuItem value="sw" sx={{ height: '30px' }}>Swahili</MenuItem>
                    <MenuItem value="fr" sx={{ height: '30px' }}>Français</MenuItem>
                  </Select>
                </div>
                <Link onClick={() => setCurrentView("resetPassword")} className="forgotPassword">{t('forgotPassword')}</Link>
              </form>
            </div>
          </div>}
        {currentView === "resetPassword" &&
          <div className="forgot-pw-content">
            <div className="input-item-with-label">
              <input
                id="username"
                name="username"
                type="text"
                placeholder={t('username').toUpperCase()}
                value={username}
                className="form-input-item"
                style={{ width: "400px" }}
                onChange={(e) => setUsername(e.target.value)}
              />
            </div>
            {showError && <span className="error"> {error} </span>}
            <CustomLoadingButtonFilled
              width={"400px"}
              disabled={!username}
              onClick={(e) => handleRequestOtp(e)}>
              {t("requestCode")}
            </CustomLoadingButtonFilled>
            {otpView &&
              <div className='otp-view'>
                <p>Enter code sent to your email</p>
                <OtpInput
                  value={otp}
                  onChange={handleOtpChange}
                  numInputs={6}
                  renderSeparator={<span>-</span>}
                  renderInput={(props) => <input {...props} />}
                  inputStyle={{
                    width: "40px",
                    height: "40px",
                    fontSize: "20px",
                    borderRadius: "4px",
                    border: "1px solid #ccc",
                    textAlign: "center",
                  }}
                />
                {otpError && <span className="error"> {otpError} </span>}
              </div>}
          </div>}

        {currentView === "verifyPassword" &&
          <div className="forgot-pw-content">
            <div className="input-item-with-label" style={{ position: "relative" }}>
              <input
                ref={newPasswordRef}
                id="enteredResetPassword"
                name="enteredResetPassword"
                type={showPassword ? "text" : "password"}
                placeholder={t('newPassword').toUpperCase()}
                value={enteredResetPassword}
                className="form-input-item"
                style={{ width: "400px", paddingRight: "30px" }}
                onChange={(e) => setEnteredResetPassword(e.target.value)}
                onBlur={(e) => {
                  try {
                    if (e.target.value.length < 5) {
                      setPasswordError("Password must be at least 6 Characters");
                    } else {
                      setPasswordError("");
                    }
                  } catch (error) {
                    console.log(error);
                  }
                }}
              />
              <span
                onClick={() => setShowPassword((prev) => !prev)}
                style={{
                  position: "absolute",
                  right: "8px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  cursor: "pointer",
                  fontSize: "0.9rem",
                }}
              >
                {showPassword ? <IoEyeOff size={20} /> : <IoEye size={20} />}
              </span>
            </div>
            <div className="input-item-with-label" style={{ position: "relative", marginBottom: "10px" }}>
              <input
                id="enteredVerifyPassword"
                name="enteredVerifyPassword"
                type={showVerifyPassword ? "text" : "password"}
                placeholder={t('verifyPassword').toUpperCase()}
                value={enteredVerifyPassword}
                className="form-input-item"
                style={{ width: "400px", paddingRight: "30px" }}
                onChange={(e) => setEnteredVerifyPassword(e.target.value)}
                onBlur={(e) => {
                  try {
                    if (e.target.value !== enteredResetPassword) {
                      setPasswordError("Passwords do not match");
                      setEnableReset(false);
                    } else {
                      setPasswordError("");
                      setEnableReset(true);
                    }
                  } catch (error) {
                    console.log(error);
                  }
                }}
              />
              <span
                onClick={() => setShowVerifyPassword((prev) => !prev)}
                style={{
                  position: "absolute",
                  right: "8px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  cursor: "pointer",
                  fontSize: "0.9rem",
                }}
              >
                {showVerifyPassword ? <IoEyeOff size={20} /> : <IoEye size={20} />}
              </span>
            </div>
            {passwordError && <span className="error"> {passwordError} </span>}
            <span style={{ marginTop: "30px" }} />
            <CustomLoadingButtonFilled
              width={"400px"}
              disabled={!enableReset}
              onClick={(e) => handleResetPassword(e)}>
              {t("reset")}
            </CustomLoadingButtonFilled>
          </div>}
        <p>
          {/* {t('oneNation')} | {t('oneFuture')} | {t('oneHealth')} */}
          {t('unifiedSystem')} | {t('seamlessOperations')} | {t('trustedOutcomes')}
        </p>
      </motion.div>
      {loading && (
        <motion.div
          className="processing-dialog"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, transition: { duration: 1 } }}
          exit={{ opacity: 0, transition: { duration: 1 } }}
        >
          <div className="processing-wrapper">
            <span className='main-title'>{isProcessingTitle || t('pleaseWait')}</span>
            <ScaleLoader
              color="#bd97fc"
              style={{
                height: "50px",
                width: "50px",
              }}
            />
          </div>
        </motion.div>
      )}
      {isProcessing && (
        <motion.div
          className="processing-dialog"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, transition: { duration: 1 } }}
          exit={{ opacity: 0, transition: { duration: 1 } }}
        >
          <div className="processing-wrapper">
            <span className='main-title'>{isProcessingTitle || t('pleaseWait')}</span>
            <ScaleLoader color="#bd97fc"
              style={{
                height: "50px",
                width: "50px",
              }}
            />
          </div>
        </motion.div>)}
    </div>
  );
};

export default Login;